<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Ref,
} from 'vue';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import {
  getFileFromUrl,
  ObjectRotator,
} from '@therobotcompany/robot-visualizer';
import { useRoute, useRouter } from 'vue-router';
import {
  type CADModelData,
  type VisionProject,
  type ProjectMember,
  type Deployment,
  DeploymentStatus,
  type ProjectTrainingRun,
  type AdvancedSettings,
  TrainingStatus,
  ACTIVE_TRAINING_STATUSES,
  VisionProjectType,
} from '@/models/RobVision';
import {
  loadUserProjects,
  loadCADData,
  unarchiveProject,
  getProjectMembers,
  removeProjectMember,
  listProjectTrainings,
  getProjectDeployments,
  loadImageAnnotations,
  fetchMaskData,
  getCustomBackgrounds,
  getRealWorldDataStatus,
  getTrainingAllowed,
  getAdvancedSettings,
  type RealWorldDataStatus,
  type TrainingAllowed,
} from '@/api/RobVisionApi';
import { cropDataURLByMask, projectTypeIcon } from '@/helpers/RobvisionHelper';
import { openRobDocs } from '@/helpers/RobDocs';
import ArchiveProjectDialog from '@/components/robvision/ArchiveProjectDialog.vue';
import ImageProjectOverview from '@/components/robvision/ImageProjectOverview.vue';
import AdvancedSettingsDisplay from '@/components/robvision/AdvancedSettingsDisplay.vue';
import ConfirmProjectTrainingDialog from '@/components/robvision/ConfirmProjectTrainingDialog.vue';
import CreateDeploymentDialog from '@/components/robvision/CreateDeploymentDialog.vue';
import AddMemberDialog from '@/components/robvision/AddMemberDialog.vue';
import LabelingImagesSection from '@/components/robvision/labeling/LabelingImagesSection.vue';
import ModelVersionsSection from '@/components/robvision/ModelVersionsSection.vue';
import useAuthStore from '@/store/AuthStore';
import useBreadcrumbStore from '@/store/BreadcrumbStore';
import { useAlertsStore } from '@/store/AlertsStore';
import gettext from '@/i18n/gettext';

const { $gettext, $ngettext } = gettext;

const route = useRoute();
const router = useRouter();
const loadingSpinnerStore = useLoadingSpinnerStore();
const breadcrumbStore = useBreadcrumbStore();
const alertsStore = useAlertsStore();
const authStore = useAuthStore();

const projectUuid = route.params.projectUuid as string;
const project: Ref<VisionProject | null> = ref(null);

const TAB_OVERVIEW = 'overview';
const TAB_SETUP = 'basemodel';
const TAB_MODELS = 'models';
const TAB_LABELING = 'labeling';
const TAB_MEMBERS = 'members';

const isValidTab = (value: unknown): value is string =>
  typeof value === 'string' &&
  [TAB_OVERVIEW, TAB_SETUP, TAB_MODELS, TAB_LABELING, TAB_MEMBERS].includes(
    value,
  );

const tab = ref(isValidTab(route.query.tab) ? route.query.tab : TAB_OVERVIEW);

// One-way latch: true once the Base Model tab has been shown. The 3D viewer
// sizes its canvas from its container at mount and only re-measures on window
// resize, so it must mount one tick after the tab becomes visible — and it
// stays mounted afterwards to avoid reloading the model on tab switches.
const baseModelTabMounted = ref(tab.value === TAB_SETUP);

// Tab -> URL: keep the active tab deep-linkable, and latch the Base Model
// mount once that tab first becomes visible.
watch(tab, async (newTab) => {
  if (route.query.tab !== newTab) {
    router.replace({ query: { ...route.query, tab: newTab } });
  }
  if (newTab === TAB_SETUP) {
    await nextTick();
    baseModelTabMounted.value = true;
  }
  if (newTab === TAB_MODELS) {
    // The window item stays mounted, so runs/images fetched onMounted go
    // stale — e.g. images marked Ready in the Labeling tab in between.
    modelsSection.value?.refresh();
  }
});

// URL -> tab: follow external query changes and normalise unknown values
// (e.g. ?tab=xyz becomes ?tab=overview instead of silently sticking around).
watch(
  () => route.query.tab,
  (queryTab) => {
    const target = isValidTab(queryTab) ? queryTab : TAB_OVERVIEW;
    if (tab.value !== target) {
      tab.value = target;
    }
    if (queryTab !== undefined && !isValidTab(queryTab)) {
      router.replace({ query: { ...route.query, tab: target } });
    }
  },
  { immediate: true },
);

// Archived projects are read-only: the backend rejects all data mutations
// for them, so the action buttons are disabled until the project is unarchived.
const isArchived = computed(() => !!project.value?.archived);

const loading = ref(false);
const cadModelData = ref<CADModelData>();
const archiveDialog = ref(false);
const triggerTrainingDialog = ref(false);
const createDeploymentDialog = ref(false);
// Set when a specific run was picked in the Models table (rollback path);
// null = deploy the newest successful run.
const deployTarget = ref<{ runId: string; isRollback: boolean } | null>(null);

function openDeploymentDialog(target?: { runId: string; isRollback: boolean }) {
  deployTarget.value = target ?? null;
  createDeploymentDialog.value = true;
}
const modelsSection = ref<InstanceType<typeof ModelVersionsSection> | null>(
  null,
);
const labelingSection = ref<InstanceType<typeof LabelingImagesSection> | null>(
  null,
);

// "Train New Model Version" on the Models tab: same flow as on the Labeling
// tab — switch there and open the fine-tune wizard (it needs the image list,
// which lives in that section).
async function openTrainWizard() {
  tab.value = TAB_LABELING;
  await nextTick();
  labelingSection.value?.openWizard();
}
// Status only (feeds the Overview stat card) — the dedicated upload/download
// UI is gone, the labeling wizard's dataset build writes this data now.
const realWorldData: Ref<RealWorldDataStatus | null> = ref(null);
const trainingAllowed: Ref<TrainingAllowed | null> = ref(null);
const advancedSettings: Ref<AdvancedSettings | null> = ref(null);
// All runs, newest first. The latest run drives the status card; whether a
// deployable model EXISTS must look at every run — users can go straight to
// a refined model, and a canceled latest run doesn't unmake earlier models.
const trainingRuns: Ref<ProjectTrainingRun[]> = ref([]);
const projectDeployments: Ref<Deployment[]> = ref([]);
const currentPartFile: Ref<File | undefined> = ref(undefined);
// Object dimensions in millimetres [width, height, depth] from the visualizer
const cadDimensions = ref<[number, number, number] | null>(null);
const imageClasses = ref<{ [key: number]: string }>({});
const imageSizes = ref<{
  [key: number]: { width: number; height: number; depth: number };
}>({});
const cutouts = ref<{ [key: number]: string | null }>({});
const croppedCutout = ref<{ [key: number]: string | null }>({});
const customBackgroundUrls = ref<string[]>([]);
const enlargedBackgroundUrl = ref<string | null>(null);
const members = ref<ProjectMember[]>([]);
const membersLoaded = ref(false);
const addMemberDialog = ref(false);
const leaveProjectDialog = ref(false);
const leavingProject = ref(false);

async function reloadProjectData() {
  if (!project.value) {
    return;
  }
  loading.value = true;
  try {
    if (project.value.project_type === VisionProjectType.CADPicking) {
      const [firstCADModel] = await loadCADData(projectUuid);
      cadModelData.value = firstCADModel;
      showProject();
    } else {
      const annotations = await loadImageAnnotations(projectUuid);

      // Reset state
      cutouts.value = {};
      croppedCutout.value = {};
      imageClasses.value = {};
      imageSizes.value = {};

      await Promise.all(
        annotations.map(async (annotation, index) => {
          const {
            cutout_url: cutoutUrl,
            segmentation_url: segmentationUrl,
            label,
            size,
          } = annotation;

          imageClasses.value[index] = label;
          imageSizes.value[index] = {
            width: size[0],
            height: size[1],
            depth: size[2],
          };

          // Default fallback
          cutouts.value[index] = cutoutUrl ?? null;

          // Apply mask-based cropping if possible
          if (cutoutUrl && segmentationUrl) {
            try {
              const mask = await fetchMaskData(segmentationUrl);
              const cropped = await cropDataURLByMask(cutoutUrl, mask);
              if (cropped) {
                croppedCutout.value[index] = cropped;
              }
            } catch (err) {
              console.warn(`Mask processing failed for index ${index}:`, err);
            }
          }
        }),
      );
    }
    trainingRuns.value = await listProjectTrainings(projectUuid);
    if (hasSuccessfulModel.value) {
      projectDeployments.value = await getProjectDeployments(projectUuid);
    }
    await Promise.all([
      reloadRealWorldDataStatus(),
      reloadTrainingAllowed(),
      reloadAdvancedSettings(),
      reloadCustomBackgrounds(),
    ]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err.message || 'Error fetching part data');
  } finally {
    loading.value = false;
  }
}

function showProject() {
  currentPartFile.value = undefined;
  cadDimensions.value = null;
  if (cadModelData.value) {
    getFileFromUrl(cadModelData.value?.file, 'part.glb')
      .then((response: File) => {
        currentPartFile.value = response;
      })
      .catch((e: unknown) => {
        currentPartFile.value = undefined;
        console.error(e);
      });
  }
}

// Dimensions come from the visualizer once the model loads, via its
// @update:objectSize event (see the template) — the single source of truth.
function onObjectSize(size: [number, number, number]) {
  cadDimensions.value = [
    Math.round(size[0] * 1000),
    Math.round(size[1] * 1000),
    Math.round(size[2] * 1000),
  ];
}

async function reloadTrainingAllowed() {
  try {
    trainingAllowed.value = await getTrainingAllowed(projectUuid);
  } catch (err) {
    console.error((err as Error).message);
    trainingAllowed.value = null;
  }
}

async function reloadAdvancedSettings() {
  try {
    advancedSettings.value = await getAdvancedSettings(projectUuid);
  } catch (err) {
    // fallback instead of failure
    console.error((err as Error).message);
    advancedSettings.value = null;
  }
}

async function reloadRealWorldDataStatus() {
  try {
    realWorldData.value = await getRealWorldDataStatus(projectUuid);
  } catch (err) {
    console.error((err as Error).message);
    realWorldData.value = null;
  }
}

async function reloadCustomBackgrounds() {
  try {
    customBackgroundUrls.value = await getCustomBackgrounds(projectUuid);
  } catch (err) {
    console.error((err as Error).message);
    customBackgroundUrls.value = [];
  }
}

function onProjectArchived() {
  alertsStore.success(
    $gettext('Project "%{ name }" archived', {
      name: project.value?.name ?? '',
    }),
  );
  router.push('/robvision');
}

async function unarchiveCurrentProject() {
  if (!project.value) {
    return;
  }
  try {
    await unarchiveProject(projectUuid);
    project.value.archived = false;
    setBreadcrumbActions();
    alertsStore.success(
      $gettext('Project "%{ name }" unarchived', {
        name: project.value.name,
      }),
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    alertsStore.error(err.message);
  }
}

function setBreadcrumbActions() {
  if (!project.value) {
    return;
  }
  breadcrumbStore.setActions(
    project.value.archived
      ? [
          {
            title: $gettext('Unarchive Project'),
            icon: 'mdi-archive-arrow-up-outline',
            handler: unarchiveCurrentProject,
          },
        ]
      : [
          {
            title: $gettext('Archive Project'),
            icon: 'mdi-archive-arrow-down-outline',
            handler: () => {
              archiveDialog.value = true;
            },
          },
        ],
  );
}

async function reloadMembers() {
  try {
    members.value = await getProjectMembers(projectUuid);
  } catch (err) {
    console.error((err as Error).message);
    members.value = [];
  } finally {
    membersLoaded.value = true;
  }
}

function onMemberAdded() {
  alertsStore.success($gettext('Member added'));
  reloadMembers();
}

const isCurrentUser = (member: ProjectMember) =>
  member.userUuid === authStore.user.uuid;

// The backend only allows self-removal; the last member leaving would
// soft-delete the project, so we point that case to "Archive Project" instead.
const leaveDisabled = computed(() => members.value.length <= 1);

async function leaveProject() {
  if (leavingProject.value) {
    return;
  }
  leavingProject.value = true;
  try {
    await removeProjectMember(projectUuid, authStore.user.uuid);
    alertsStore.success(
      $gettext('You left the project "%{ name }"', {
        name: project.value?.name ?? '',
      }),
    );
    router.push('/robvision');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    alertsStore.error(err.message);
  } finally {
    leavingProject.value = false;
    leaveProjectDialog.value = false;
  }
}

function memberInitials(member: ProjectMember): string {
  const source = (member.name ?? '').trim() || member.email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

const AVATAR_COLORS = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
];

function memberColor(member: ProjectMember): string {
  const hash = [...member.userUuid].reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const normalizedTrainingStatus = computed(() => {
  const status = trainingRuns.value[0]?.status;
  if (!status) {
    return null;
  }
  const knownStatuses = Object.values(TrainingStatus) as string[];
  return knownStatuses.includes(status) ? status : TrainingStatus.UNKNOWN;
});

const hasSuccessfulModel = computed(() =>
  trainingRuns.value.some((run) => run.status === TrainingStatus.SUCCEEDED),
);

const trainingInProgress = computed(
  () =>
    normalizedTrainingStatus.value !== null &&
    ACTIVE_TRAINING_STATUSES.includes(normalizedTrainingStatus.value),
);

// The status used to freeze until a manual page refresh — poll while a run
// is active so the card flips to finished/failed on its own.
let trainingPollTimer: ReturnType<typeof setInterval> | null = null;

async function pollTrainingStatus() {
  try {
    trainingRuns.value = await listProjectTrainings(projectUuid);
    if (!trainingInProgress.value) {
      // Terminal state reached: refresh what depends on it.
      await reloadTrainingAllowed();
      if (hasSuccessfulModel.value) {
        projectDeployments.value = await getProjectDeployments(projectUuid);
      }
    }
  } catch (err) {
    console.error((err as Error).message);
  }
}

watch(
  trainingInProgress,
  (active) => {
    if (active && !trainingPollTimer) {
      trainingPollTimer = setInterval(pollTrainingStatus, 15_000);
    } else if (!active && trainingPollTimer) {
      clearInterval(trainingPollTimer);
      trainingPollTimer = null;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (trainingPollTimer) {
    clearInterval(trainingPollTimer);
  }
});

// A training or deployment was just started — refresh both the view's own
// training state and the model-versions table.
async function onModelsChanged() {
  modelsSection.value?.refresh();
  await reloadProjectData();
}

interface StatCard {
  caption: string;
  value: string;
  subtitle: string;
  color: string;
}

const statCards = computed((): StatCard[] => {
  const status = normalizedTrainingStatus.value;
  let model: Omit<StatCard, 'caption'>;
  if (!status) {
    model = {
      value: $gettext('None'),
      subtitle: $gettext('No training started yet'),
      color: 'grey',
    };
  } else if (
    status === TrainingStatus.RUNNING ||
    status === TrainingStatus.STARTING
  ) {
    model = {
      value: $gettext('In Training'),
      subtitle: $gettext('The model appears here once training finishes'),
      color: 'info',
    };
  } else if (status === TrainingStatus.CANCELING) {
    model = {
      value: $gettext('Canceling'),
      subtitle: $gettext('Waiting for the training to stop'),
      color: 'grey',
    };
  } else if (status === TrainingStatus.SUCCEEDED) {
    model = {
      value: $gettext('Ready'),
      subtitle: $gettext('Training finished, ready to deploy'),
      color: 'success',
    };
  } else if (status === TrainingStatus.CANCELED) {
    // Keep in sync with the Models table, which shows canceled runs as a
    // neutral "Canceled" — not as a failure.
    model = {
      value: $gettext('Canceled'),
      subtitle: hasSuccessfulModel.value
        ? $gettext('Last training was canceled — an earlier model is available')
        : $gettext('Last training was canceled'),
      color: 'grey',
    };
  } else {
    model = {
      value: $gettext('Failed'),
      subtitle: hasSuccessfulModel.value
        ? $gettext('Last training failed — an earlier model is available')
        : $gettext('Last training failed'),
      color: 'error',
    };
  }

  // Only CUs where a deployment actually succeeded count as "deployed" —
  // failed and in-flight ones don't run the model.
  const cuCount = deployedCuCount.value;
  const hasRwd = !!realWorldData.value?.realWorldData;

  return [
    { caption: $gettext('Latest Model'), ...model },
    {
      caption: $gettext('Deployed On'),
      value:
        cuCount === 0
          ? $gettext('Not Deployed')
          : $ngettext('%{ n } CU', '%{ n } CUs', cuCount, {
              n: String(cuCount),
            }),
      subtitle:
        cuCount === 0
          ? $gettext('Deploy a finished version to a robot')
          : $gettext('Running the trained model'),
      color: cuCount === 0 ? 'grey' : 'success',
    },
    {
      caption: $gettext('Real World Data'),
      value: hasRwd ? $gettext('Uploaded') : $gettext('None Yet'),
      subtitle: hasRwd
        ? $gettext('Dataset available for retraining')
        : $gettext('Upload a dataset to enable retraining'),
      color: hasRwd ? 'success' : 'grey',
    },
  ];
});

interface WorkflowStep {
  icon: string;
  title: string;
  subtitle: string;
  hint: string;
  done: boolean;
  locked: boolean;
  running?: boolean;
}

const deployedCuCount = computed(
  () =>
    projectDeployments.value.filter(
      (deployment) => deployment.status === DeploymentStatus.Success,
    ).length,
);

const workflowSteps = computed((): WorkflowStep[] => {
  // Any successful run counts — a canceled retrain must not re-lock the
  // deploy step while deployable models exist.
  const trained = hasSuccessfulModel.value || deployedCuCount.value > 0;
  const deployed = deployedCuCount.value > 0;
  return [
    {
      icon: 'mdi-cube-scan',
      title: $gettext('Train Base Model'),
      subtitle: $gettext('Trains automatically — no labeling needed.'),
      // Offering "Start Training" while a run is active reads as if nothing
      // is happening — mirror the status card instead.
      hint: trained
        ? $gettext('View Model')
        : trainingInProgress.value
          ? $gettext('Training in Progress')
          : $gettext('Start Training'),
      done: trained,
      locked: false,
      running: trainingInProgress.value,
    },
    {
      icon: 'mdi-robot-industrial',
      title: $gettext('Deploy to Robot'),
      subtitle: $gettext('Roll out to one or more control units.'),
      hint: !trained
        ? $gettext('Needs a Finished Training')
        : deployed
          ? $gettext('Deploy Again')
          : $gettext('Deploy'),
      done: deployed,
      locked: !trained,
    },
  ];
});

onMounted(async () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const projects = await loadUserProjects(true);
    project.value = projects.find((p) => p.uuid === projectUuid) ?? null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    // A failed request is not "project not found" — report it as such
    console.error(err.message || 'Error fetching projects');
    alertsStore.error($gettext('Could not load Vision Project'));
    router.push('/robvision');
    return;
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
  if (!project.value) {
    alertsStore.error($gettext('Vision Project not found'));
    router.push('/robvision');
    return;
  }
  breadcrumbStore.setDynamicItems([{ title: project.value.name }]);
  setBreadcrumbActions();
  await Promise.all([reloadProjectData(), reloadMembers()]);
});
</script>

<template>
  <div v-if="project" class="pa-4 pa-sm-12 centered-page vision-project">
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <v-avatar color="secondary" rounded="lg" size="44" class="mr-4">
          <v-icon :icon="projectTypeIcon(project.project_type)" />
        </v-avatar>
        <div>
          <div class="d-flex align-center">
            <h1>{{ project.name }}</h1>
            <v-chip class="ml-3" size="small" density="comfortable">
              {{ project.archived ? $gettext('Archived') : $gettext('Active') }}
            </v-chip>
          </div>
        </div>
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          size="24"
          class="ml-4"
        />
        <v-spacer />
      </v-col>
    </v-row>

    <v-tabs v-model="tab" class="mt-6" color="dusk-blue">
      <v-tab :value="TAB_OVERVIEW">{{ $gettext('Overview') }}</v-tab>
      <v-tab :value="TAB_SETUP">{{ $gettext('Setup') }}</v-tab>
      <v-tab :value="TAB_MODELS">{{ $gettext('Models') }}</v-tab>
      <v-tab :value="TAB_LABELING">{{ $gettext('Labeling') }}</v-tab>
      <v-tab :value="TAB_MEMBERS">{{ $gettext('Members') }}</v-tab>
    </v-tabs>
    <v-divider />

    <v-window v-model="tab" class="mt-3">
      <!-- OVERVIEW TAB -->
      <v-window-item :value="TAB_OVERVIEW">
        <v-row>
          <v-col v-for="card in statCards" :key="card.caption" cols="12" md="4">
            <v-card rounded="lg" class="h-100">
              <v-card-subtitle class="text-uppercase pt-4">
                {{ card.caption }}
              </v-card-subtitle>
              <v-card-title class="d-flex align-center py-0">
                <v-icon
                  icon="mdi-circle"
                  size="10"
                  :color="card.color"
                  class="mr-2"
                />
                {{ card.value }}
              </v-card-title>
              <v-card-text class="text-caption text-medium-emphasis">
                {{ card.subtitle }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col cols="12" class="d-flex align-end justify-space-between">
            <div>
              <h3>{{ $gettext('How It Works') }}</h3>
              <p class="text-body-2 text-medium-emphasis mb-0">
                {{
                  $gettext('Train the base model, then deploy it to a robot.')
                }}
              </p>
            </div>
            <r-btn
              variant="outlined"
              icon="mdi-help-circle-outline"
              @click="openRobDocs"
            >
              {{ $gettext('Help') }}
            </r-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card rounded="lg" class="pa-4">
              <v-list bg-color="transparent" class="pa-0">
                <v-list-item
                  v-for="(step, index) in workflowSteps"
                  :key="index"
                  :title="step.title"
                  :subtitle="step.subtitle"
                  :disabled="step.locked"
                  :active="step.done"
                  color="success"
                  border
                  rounded="lg"
                  lines="two"
                  class="mb-2"
                  @click="tab = TAB_MODELS"
                >
                  <template #prepend>
                    <v-avatar
                      size="34"
                      :color="step.done ? 'success' : 'surface-variant'"
                    >
                      <v-icon
                        :icon="step.locked ? 'mdi-lock-outline' : step.icon"
                        size="small"
                      />
                    </v-avatar>
                  </template>
                  <template #append>
                    <v-progress-circular
                      v-if="step.running"
                      indeterminate
                      size="14"
                      width="2"
                      class="mr-2"
                    />
                    <span class="text-caption text-medium-emphasis mr-2">
                      {{ step.hint }}
                    </span>
                    <v-icon icon="mdi-chevron-right" size="small" />
                  </template>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- BASE MODEL TAB -->
      <v-window-item :value="TAB_SETUP">
        <v-row>
          <v-col cols="12">
            <h3>{{ $gettext('Setup') }}</h3>
            <p class="text-body-2 text-medium-emphasis mb-0">
              {{
                $gettext(
                  'The part definition and settings every training run for this project uses.',
                )
              }}
            </p>
          </v-col>
        </v-row>
        <v-row v-if="currentPartFile" class="mt-2">
          <v-col cols="12">
            <!-- The card frames the viewer (ROI-1298); the plain wrapper div
                 inside stays: the rotator sizes its canvas from the element
                 two levels above the canvas, which must have the content
                 width -->
            <v-card v-if="baseModelTabMounted" rounded="lg" class="pa-3">
              <div>
                <object-rotator
                  v-model:object-file-or-url="currentPartFile"
                  style="height: 500px"
                  :container-border-radius="12"
                  :object-scale="1"
                  :object-shift="[0, 0, 0]"
                  @update:object-size="onObjectSize"
                />
              </div>
            </v-card>
            <div v-if="cadDimensions" class="mt-3">
              <span class="text-caption text-medium-emphasis">
                {{ $gettext('Object Dimensions (W × H × D)') }}
              </span>
              <div class="text-body-1 font-weight-medium">
                {{ cadDimensions[0] }} × {{ cadDimensions[1] }} ×
                {{ cadDimensions[2] }} mm
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="project.project_type !== VisionProjectType.CADPicking"
          class="mt-4"
        >
          <v-col cols="12">
            <ImageProjectOverview
              :thumbnails="croppedCutout"
              :image-classes="imageClasses"
              :image-sizes="imageSizes"
            />
          </v-col>
        </v-row>
        <v-row v-if="customBackgroundUrls.length > 0" class="mt-4">
          <v-col cols="12">
            <h3 class="ma-2">
              {{ $gettext('Backgrounds') }}
            </h3>
            <div class="custom-bg-thumbs">
              <v-img
                v-for="(url, index) in customBackgroundUrls"
                :key="`custom-bg-${index}`"
                :src="url"
                class="custom-bg-thumb"
                width="96"
                height="96"
                cover
                rounded="lg"
                role="button"
                :aria-label="$gettext('Enlarge background image')"
                tabindex="0"
                @click="enlargedBackgroundUrl = url"
                @keydown.enter="enlargedBackgroundUrl = url"
              >
                <template #placeholder>
                  <div class="d-flex align-center justify-center fill-height">
                    <v-progress-circular indeterminate size="20" width="2" />
                  </div>
                </template>
              </v-img>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="advancedSettings" class="mt-4">
          <v-col>
            <h3 class="ma-2">{{ $gettext('Advanced Settings') }}</h3>
            <AdvancedSettingsDisplay
              :settings="advancedSettings"
              :show-c-a-d-options="
                project.project_type === VisionProjectType.CADPicking
              "
              :texture="cadModelData?.texture"
              :rgba-value="cadModelData?.rgba_value"
            />
          </v-col>
        </v-row>
      </v-window-item>

      <!-- MODELS TAB -->
      <v-window-item :value="TAB_MODELS">
        <model-versions-section
          ref="modelsSection"
          :project="project"
          :is-archived="isArchived"
          :training-allowed="!!trainingAllowed?.allowed"
          @retrain="triggerTrainingDialog = true"
          @deploy="openDeploymentDialog"
          @train-new="openTrainWizard"
          @training-triggered="pollTrainingStatus"
        />
      </v-window-item>

      <!-- LABELING TAB -->
      <v-window-item :value="TAB_LABELING">
        <labeling-images-section
          ref="labelingSection"
          :project-uuid="projectUuid"
          :project-name="project?.name"
          :is-archived="isArchived"
          :training-in-progress="trainingInProgress"
          @training-triggered="onModelsChanged"
        />
      </v-window-item>

      <!-- MEMBERS TAB -->
      <v-window-item :value="TAB_MEMBERS">
        <v-row>
          <v-col cols="12" class="d-flex align-end justify-space-between">
            <div>
              <h3>{{ $gettext('Members') }}</h3>
              <p class="text-body-2 text-medium-emphasis mb-0">
                {{
                  $gettext(
                    'Manage who has access to this project and add new members.',
                  )
                }}
              </p>
            </div>
            <r-btn
              variant="accent"
              icon="mdi-account-plus"
              :disabled="isArchived"
              @click="addMemberDialog = true"
            >
              {{ $gettext('Add Member') }}
            </r-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-card rounded="lg">
              <v-table>
                <thead>
                  <tr>
                    <th>{{ $gettext('Name') }}</th>
                    <th>{{ $gettext('Email') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="member in members" :key="member.userUuid">
                    <td>
                      <div class="d-flex align-center py-2">
                        <v-avatar
                          :color="memberColor(member)"
                          size="36"
                          class="mr-3"
                        >
                          <span class="text-body-2">
                            {{ memberInitials(member) }}
                          </span>
                        </v-avatar>
                        <span class="font-weight-medium">
                          {{ member.name || member.email }}
                        </span>
                        <v-chip
                          v-if="isCurrentUser(member)"
                          class="ml-2"
                          size="small"
                          color="info"
                          density="comfortable"
                        >
                          {{ $gettext('You') }}
                        </v-chip>
                      </div>
                    </td>
                    <td>{{ member.email }}</td>
                    <td class="text-right">
                      <v-menu
                        v-if="isCurrentUser(member)"
                        location="bottom end"
                      >
                        <template v-slot:activator="{ props: menuProps }">
                          <v-btn
                            v-bind="menuProps"
                            icon="mdi-dots-vertical"
                            variant="text"
                            size="small"
                            :aria-label="$gettext('Member actions')"
                          />
                        </template>
                        <v-list density="compact">
                          <v-tooltip
                            location="bottom"
                            :disabled="!leaveDisabled"
                          >
                            <template
                              v-slot:activator="{ props: tooltipProps }"
                            >
                              <span v-bind="tooltipProps">
                                <v-list-item
                                  prepend-icon="mdi-location-exit"
                                  :title="$gettext('Leave Project')"
                                  :disabled="leaveDisabled || isArchived"
                                  @click="leaveProjectDialog = true"
                                />
                              </span>
                            </template>
                            <span>
                              {{
                                $gettext(
                                  'You are the only member. Archive the project instead.',
                                )
                              }}
                            </span>
                          </v-tooltip>
                        </v-list>
                      </v-menu>
                    </td>
                  </tr>
                  <tr v-if="members.length === 0">
                    <td colspan="3" class="text-center text-medium-emphasis">
                      {{
                        membersLoaded
                          ? $gettext('No members')
                          : $gettext('Loading') + '...'
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>
  </div>

  <template v-if="project">
    <ArchiveProjectDialog
      v-model:isOpen="archiveDialog"
      :project="project"
      @project-archived="onProjectArchived"
    />

    <ConfirmProjectTrainingDialog
      v-model:isOpen="triggerTrainingDialog"
      :project="project"
      :training-allowed="trainingAllowed"
      @training-triggered="onModelsChanged"
    />

    <template v-if="createDeploymentDialog">
      <CreateDeploymentDialog
        v-model:isOpen="createDeploymentDialog"
        :project="project"
        :run-id="deployTarget?.runId ?? null"
        @deployment-created="onModelsChanged"
      />
    </template>

    <AddMemberDialog
      v-model:isOpen="addMemberDialog"
      :project="project"
      @member-added="onMemberAdded"
    />

    <v-dialog
      v-model="leaveProjectDialog"
      max-width="600"
      @click:outside="leaveProjectDialog = false"
    >
      <v-card>
        <v-card-title>{{ $gettext('Leave Project') }}</v-card-title>
        <v-card-text>
          <p>
            {{
              $gettext(
                'Are you sure you want to leave this project? You will lose access to it.',
              )
            }}
          </p>
          <p>
            <b>{{ project.name }}</b>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            rounded="pill"
            @click="leaveProjectDialog = false"
          >
            {{ $gettext('Cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="red-darken-1"
            variant="text"
            rounded="pill"
            :loading="leavingProject"
            @click="leaveProject"
          >
            {{ $gettext('Leave') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </template>

  <v-dialog
    :model-value="enlargedBackgroundUrl !== null"
    max-width="1200"
    @update:model-value="enlargedBackgroundUrl = null"
  >
    <v-card class="enlarged-bg-card">
      <v-btn
        icon="mdi-close"
        variant="text"
        size="small"
        class="enlarged-bg-close"
        :aria-label="$gettext('Close')"
        @click="enlargedBackgroundUrl = null"
      />
      <img
        v-if="enlargedBackgroundUrl"
        :src="enlargedBackgroundUrl"
        class="enlarged-bg-img"
        alt=""
      />
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* The window clips its content (needed for the slide transition), which
   would cut off card shadows at the edges — pad the clip area outward and
   compensate with negative side margins; mt-3 + the padding restores the
   usual 24px gap below the tabs */
.vision-project :deep(.v-window) {
  padding: 12px;
  margin: -12px;
}

.custom-bg-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 8px;
}

.custom-bg-thumb {
  background: #f1f5f9;
  cursor: pointer;
  /* v-img defaults to flex-grow inside the flex row; pin it to 96x96 */
  flex: 0 0 96px;
  max-width: 96px;
}

.enlarged-bg-card {
  position: relative;
  background: #000;
}

.enlarged-bg-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  color: #fff;
  background: rgba(0, 0, 0, 0.4);
}

.enlarged-bg-img {
  display: block;
  width: 100%;
  max-height: 85vh;
  object-fit: contain;
}
</style>
